import React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/utils/cn';
import { StatusBadge } from '@/components/ui/StatusBadge';

interface ZoneListTableProps {
  data: any[];
  onRowClick: (zone: any) => void;
  selectedId?: string;
}

export function ZoneListTable({ data, onRowClick, selectedId }: ZoneListTableProps) {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { label: 'Đang hoạt động', variant: 'success' as const };
      case 'paused':
        return { label: 'Tạm dừng', variant: 'warning' as const };
      case 'completed':
        return { label: 'Đã hoàn thành', variant: 'purple' as const };
      default:
        return { label: 'Không xác định', variant: 'default' as const };
    }
  };

  const getTypeStyle = (type: string) => {
    if (type === 'construction') {
      return 'bg-blue-50 text-blue-600 border-blue-100';
    }
    return 'bg-emerald-50 text-emerald-600 border-emerald-100';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse min-w-[1000px]">
        <thead>
          <tr className="bg-white border-b border-slate-100">
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal w-12 text-center">
              <input type="checkbox" className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            </th>
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal w-12">#</th>
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal">Mã khu vực</th>
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal">Tên khu vực</th>
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal">Dự án / Công việc</th>
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal text-center">Loại</th>
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal text-right">Diện tích (m²)</th>
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal text-center">Trạng thái</th>
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal">Cập nhật cuối</th>
            <th className="px-4 py-3 sm:text-sm text-xs text-slate-500 font-normal text-center">Thao tác</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {data.map((row, index) => {
            const statusConfig = getStatusConfig(row.status_code);
            const isSelected = selectedId === row.id;

            return (
              <tr 
                key={row.id} 
                onClick={() => onRowClick(row)}
                className={cn(
                  "hover:bg-slate-50/50 transition-colors cursor-pointer group",
                  isSelected && "bg-emerald-50/30"
                )}
              >
                <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                </td>
                <td className="px-4 py-4 sm:text-sm text-xs text-slate-400">
                  {String(index + 1).padStart(2, '0')}
                </td>
                <td className="px-4 py-4 sm:text-sm text-xs font-normal text-slate-900">
                  {row.zone_code || row.id.split('-')[0]}
                </td>
                <td className="px-4 py-4 sm:text-sm text-xs text-slate-700">
                  {row.zone_name}
                </td>
                <td className="px-4 py-4 sm:text-sm text-xs text-slate-600">
                  {row.project?.name || '-'}
                </td>
                <td className="px-4 py-4 text-center">
                  <span className={cn("px-2 py-0.5 rounded text-[11px] font-normal border", getTypeStyle(row.zone_type))}>
                    {row.zone_type === 'construction' ? 'Thi công' : 'Bảo dưỡng'}
                  </span>
                </td>
                <td className="px-4 py-4 sm:text-sm text-xs text-slate-600 text-right">
                  {row.area_m2?.toLocaleString('vi-VN') || '0'}
                </td>
                <td className="px-4 py-4 text-center">
                  <StatusBadge label={statusConfig.label} variant={statusConfig.variant} className="text-[11px] px-2 py-0.5" />
                </td>
                <td className="px-4 py-4 sm:text-sm text-xs text-slate-500">
                  {row.updated_at ? new Date(row.updated_at).toLocaleDateString('vi-VN') : '-'}
                </td>
                <td className="px-4 py-4 text-center">
                  <button 
                    onClick={(e) => e.stopPropagation()} 
                    className="w-8 h-8 inline-flex items-center justify-center rounded text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Phân trang */}
      <div className="flex items-center justify-between p-4 border-t border-slate-100">
        <span className="sm:text-sm text-xs text-slate-500">
          Hiển thị 1 - {Math.min(10, data.length)} trong tổng số {data.length} khu vực
        </span>
        <div className="flex items-center gap-2">
          <button className="w-8 h-8 rounded border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 disabled:opacity-50" disabled>
            <span className="text-sm">‹</span>
          </button>
          <button className="w-8 h-8 rounded bg-emerald-50 text-emerald-600 sm:text-sm text-xs font-normal border border-emerald-100">1</button>
          <button className="w-8 h-8 rounded hover:bg-slate-50 text-slate-600 sm:text-sm text-xs">2</button>
          <button className="w-8 h-8 rounded hover:bg-slate-50 text-slate-600 sm:text-sm text-xs">3</button>
          <span className="w-8 h-8 flex items-center justify-center text-slate-400">...</span>
          <button className="w-8 h-8 rounded border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50">
            <span className="text-sm">›</span>
          </button>
        </div>
        <div className="w-24">
          <select className="w-full h-8 px-2 border border-slate-200 rounded sm:text-sm text-xs outline-none focus:border-emerald-500 text-slate-600 bg-white">
            <option>10 / trang</option>
            <option>20 / trang</option>
            <option>50 / trang</option>
          </select>
        </div>
      </div>
    </div>
  );
}
